import { useState, cloneElement, ReactElement, forwardRef } from 'react'
import cx from 'classnames'
import Styles from './ActivityEvent.module.scss'

import { BaseActivityEventProps, ActivityEventTrackMode } from './Types'
import { Collapse } from 'common/Collapse/Collapse'
import { ReactComponent as MergeTrack } from './icons/mergeTrack.svg'
import { ReactComponent as BranchTrack } from './icons/branchTrack.svg'
import { useHover } from '@react-aria/interactions'
import { stopPropagation } from 'utils'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { PropertyValuePill } from 'components/PropertyValuePill/PropertyValuePill'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { DateTime } from 'components/DateTime/DateTime'

/*
 * BaseActivityEvent is a private component that is used to render Activity
 * Events. It is used as a template by every component that renders an event
 * type, and is agnostic to the event type. It can render an individual event
 * as well as grouped events, like edits groups or comments.
 * It is not intended to be used directly, but only rendered by the component
 * dedicated to each event type (for example, EditsGroup).
 */
const BaseActivityEvent = forwardRef<HTMLDivElement, BaseActivityEventProps>(
  (
    {
      icon,
      content,
      name,
      timestamp,
      nestedContent,
      message,
      initiallyOpen = false,
      isLast = true,
      isSelected = false,
      isDisabled = false,
      actions,
      trackMode,
      onClick,
      actionsEnabled = true,
      flashOnAppear = false,
      className,
      buttons,
      leftRailClassName,
      status,
      timestampFormat = 'll',
      showTimestamp = true,
      isStatic = false,
    },
    forwardedRef,
  ) => {
    const [hideNestedContent, setHideNestedContent] = useState(!initiallyOpen)
    const { hoverProps, isHovered } = useHover({})

    const activityEventContent = (
      <div
        ref={forwardedRef}
        className={cx(Styles.root, className, {
          [Styles.selected]: isSelected,
          [Styles.disabled]: isDisabled,
          [Styles.hovered]: isHovered && !isStatic,
          [Styles.flash]: flashOnAppear,
          [Styles.static]: isStatic,
        })}
        onClick={onClick}
        {...hoverProps}
      >
        <div
          className={cx(Styles.leftRail, leftRailClassName, {
            [Styles.last]: isLast,
            [Styles.middle]: !isLast,
            [Styles.merge]: trackMode === ActivityEventTrackMode.Merge,
            [Styles.branch]: trackMode === ActivityEventTrackMode.Branch,
          })}
        >
          {trackMode === ActivityEventTrackMode.Merge && <MergeTrack className={Styles.mergeTrack} />}
          {cloneElement(icon as ReactElement, {
            className: Styles.icon,
          })}
          {trackMode === ActivityEventTrackMode.Branch && <BranchTrack className={Styles.branchTrack} />}
        </div>
        <div className={Styles.main}>
          <div className={Styles.content}>
            <div className={Styles.contentText}>
              {name && <div className={Styles.name}>{name}</div>}
              <span>{content}</span>{' '}
              {showTimestamp && (
                <Tooltip label={<DateTime timestamp={timestamp} format="lll" />} hide={timestampFormat === 'lll'}>
                  <span className={Styles.timestamp}>
                    <DateTime timestamp={timestamp} format={timestampFormat} />
                  </span>
                </Tooltip>
              )}
              {status && (
                <div className={Styles.statusWrapper}>
                  <PropertyValuePill propertyValue={{ value: status.name, meta: { color: status.color } }} />
                </div>
              )}
              {nestedContent ? (
                <Collapse
                  collapsed={hideNestedContent}
                  onToggle={setHideNestedContent}
                  className={Styles.collapseButton}
                  aria-label="Toggle nested events"
                />
              ) : null}
            </div>
            {actionsEnabled && (
              <div className={Styles.actions} onClick={stopPropagation}>
                {actions}
              </div>
            )}
          </div>
          {message && (
            <div className={Styles.message} data-testid={`event-${name || ''}-${timestamp}`}>
              {message}
            </div>
          )}
          {buttons && <div className={Styles.buttons}>{buttons}</div>}
          {!!nestedContent && !hideNestedContent && <div className={Styles.nestedContent}>{nestedContent}</div>}
        </div>
      </div>
    )

    return (
      <ConditionalWrapper
        wrapper={(children) => (
          <Tooltip placement="top" label="This event is already selected">
            {children}
          </Tooltip>
        )}
        condition={isDisabled}
      >
        {activityEventContent}
      </ConditionalWrapper>
    )
  },
)

export { BaseActivityEvent }
